<template>
  <!-- Always on screen, quiet when there is nothing to report. It was hidden
       at zero at first, and then nobody could find it when they needed it —
       you cannot learn where a thing lives if it only appears once you are
       already in trouble. -->
  <div class="problem-log">
    <button
      class="problem-button"
      ref="button"
      :class="{ open, quiet: entries.length === 0 }"
      v-on:click.stop="toggle"
      :title="
        entries.length > 0
          ? entries.length + ' problem(s) recorded — click to read or copy them'
          : 'Problem log — nothing has gone wrong in this browser yet'
      "
    >
      <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor">
        <path
          d="M12.866 3l9.526 16.5a1 1 0 0 1-.866 1.5H2.474a1 1 0 0 1-.866-1.5L11.134 3a1 1 0 0 1 1.732 0zM11 16v2h2v-2h-2zm0-7v5h2V9h-2z"
        />
      </svg>
      {{ entries.length }}
    </button>

    <!-- The header has a backdrop-filter, which makes it its own stacking
         context: any z-index inside it is trapped there, and the panel ended
         up behind the page content. Rendering it on <body> is the fix. -->
    <Teleport to="body">
      <div class="problem-panel" v-if="open" :style="panelStyle">
        <div class="problem-empty" v-if="entries.length === 0">
          Nothing has gone wrong in this browser yet. If the editor freezes or a
          button does nothing, come back here — whatever failed will be listed,
          and <strong>Copy all</strong> puts it on the clipboard to send over.
        </div>
        <div class="problem-head" v-if="entries.length > 0">
          <strong>What went wrong</strong>
          <span class="problem-hint">
            Newest last. Copy this and send it over — it says what failed and
            when.
          </span>
        </div>
        <div class="problem-list">
          <div
            class="problem-row"
            v-for="(entry, index) in reversed"
            :key="index"
          >
            <span class="problem-kind" :class="'kind-' + entry.kind">
              {{ entry.kind }}
            </span>
            <span class="problem-body">
              <span class="problem-title">
                {{ entry.title }}
                <em v-if="entry.count > 1">×{{ entry.count }}</em>
              </span>
              <span class="problem-detail" v-if="entry.detail">
                {{ entry.detail }}
              </span>
              <span class="problem-meta">
                {{ timeOf(entry) }} · {{ entry.where }}
              </span>
            </span>
          </div>
        </div>
        <div class="problem-actions" v-if="entries.length > 0">
          <button class="btn btn-primary btn-small" v-on:click="copy">
            {{ copied ? "Copied" : "Copy all" }}
          </button>
          <button class="btn btn-simple btn-small" v-on:click="clear">
            Clear
          </button>
        </div>
        <textarea
          class="problem-fallback"
          ref="fallback"
          v-if="fallbackText"
          :value="fallbackText"
          readonly
        ></textarea>
      </div>
    </Teleport>
  </div>
</template>

<script>
import {
  getProblems,
  clearProblems,
  onProblems,
  problemsAsText,
} from "../modules/problem-log";

export default {
  name: "ProblemLog",
  data() {
    return {
      entries: getProblems(),
      open: false,
      copied: false,
      // Filled only when the clipboard is unavailable, so there is still a
      // way to get the text out.
      fallbackText: "",
      unsubscribe: null,
      // Where to draw the teleported panel: under the button, right-aligned
      // with it.
      panelStyle: {},
    };
  },
  computed: {
    reversed() {
      return [...this.entries].reverse();
    },
  },
  mounted() {
    this.unsubscribe = onProblems((entries) => {
      this.entries = [...entries];
    });
    document.addEventListener("click", this.onDocumentClick);
  },
  unmounted() {
    this.unsubscribe?.();
    document.removeEventListener("click", this.onDocumentClick);
  },
  methods: {
    toggle() {
      this.open = !this.open;
      if (this.open) {
        this.placePanel();
      }
    },
    placePanel() {
      const rect = this.$refs.button?.getBoundingClientRect();
      if (!rect) {
        return;
      }
      this.panelStyle = {
        position: "fixed",
        top: `${Math.round(rect.bottom + 8)}px`,
        right: `${Math.round(window.innerWidth - rect.right)}px`,
      };
    },
    // Clicking anywhere else closes it — a panel over the editor that only
    // closes from its own button is in the way. The button itself stops its
    // click from reaching here, otherwise opening and closing raced.
    onDocumentClick(event) {
      if (!this.open) {
        return;
      }
      if (!event.target?.closest?.(".problem-panel")) {
        this.open = false;
      }
    },
    timeOf(entry) {
      return new Date(entry.at).toLocaleTimeString();
    },
    async copy() {
      const text = problemsAsText();
      try {
        await navigator.clipboard.writeText(text);
        this.copied = true;
        setTimeout(() => {
          this.copied = false;
        }, 1500);
      } catch (e) {
        // Clipboard access is refused in some setups; show the text instead
        // of failing quietly.
        this.fallbackText = text;
        this.$nextTick(() => this.$refs.fallback?.select());
      }
    },
    clear() {
      clearProblems();
      this.fallbackText = "";
      this.open = false;
    },
  },
};
</script>

<style scoped>
.problem-log {
  position: relative;
}
.problem-button {
  display: flex;
  align-items: center;
  gap: 4px;
  border: none;
  border-radius: 999px;
  padding: 4px 10px;
  background: #fef3c7;
  color: #92400e;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
}
.problem-button.open,
.problem-button:hover {
  background: #fde68a;
}
/* Nothing wrong: still there, but it does not ask for attention. */
.problem-button.quiet {
  background: #eef1f5;
  color: #94a3b8;
}
.problem-button.quiet:hover {
  background: #e2e8f0;
  color: #64748b;
}
.problem-empty {
  font-size: 12.5px;
  color: #64748b;
  line-height: 1.5;
}
.problem-button svg {
  display: block;
}
.problem-panel {
  /* top/right come from panelStyle — the panel lives on <body> now. */
  position: fixed;
  width: 420px;
  max-width: 80vw;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 12px 40px rgba(15, 23, 42, 0.18);
  padding: 12px;
  z-index: 4000;
}
.problem-head {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 8px;
}
.problem-hint {
  font-size: 11.5px;
  color: #94a3b8;
}
.problem-list {
  max-height: 320px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.problem-row {
  display: flex;
  gap: 8px;
  padding: 6px;
  border-radius: 8px;
  background: #f8fafc;
}
.problem-kind {
  flex: none;
  align-self: flex-start;
  font-size: 9.5px;
  font-weight: 800;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  padding: 2px 5px;
  border-radius: 5px;
  background: #e2e8f0;
  color: #475569;
}
.kind-api {
  background: #dbeafe;
  color: #1e40af;
}
.kind-js,
.kind-promise {
  background: #fee2e2;
  color: #991b1b;
}
.kind-editor {
  background: #fef3c7;
  color: #92400e;
}
.problem-body {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.problem-title {
  font-size: 12.5px;
  font-weight: 600;
  color: #16181d;
  word-break: break-word;
}
.problem-title em {
  font-style: normal;
  color: #92400e;
}
/* Details are long and only matter once you are already reading the row. */
.problem-detail {
  font-size: 11.5px;
  color: #64748b;
  word-break: break-word;
  max-height: 54px;
  overflow: hidden;
}
.problem-meta {
  font-size: 11px;
  color: #94a3b8;
}
.problem-actions {
  display: flex;
  gap: 6px;
  margin-top: 10px;
}
.problem-fallback {
  width: 100%;
  height: 120px;
  margin-top: 8px;
  font-size: 11px;
  font-family: monospace;
}
</style>
